export type ForecastArrivalKind = "ghost" | "manual" | "transfer" | "po" | "work_order";

const FORECAST_QUANTITY_ROUND_EPS = 1e-9;

export const FORECAST_ARRIVAL_KIND_ORDER: ForecastArrivalKind[] = [
  "ghost",
  "manual",
  "transfer",
  "po",
  "work_order",
];

export type ForecastArrivalDelta = {
  cpn: string;
  locationCode: string;
  monthKey: string;
  quantityDelta: number;
  kind: ForecastArrivalKind;
};

export type ForecastRerollCell = {
  month: string;
  starting_qty: number;
  arrivals: number;
  demand: number;
  ending_qty: number;
  reserve_target: number;
  shortfall: number;
  order_qty: number;
  arrivals_by_kind?: { kind: ForecastArrivalKind; quantity: number }[];
};

export type ForecastRerollSeries = {
  cpn: string;
  location_code: string;
  order_multiple: number;
  allocated_reserve: number;
  cells: ForecastRerollCell[];
};

function roundUpWholeUnit(value: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  if (parsed >= 0) return Math.ceil(parsed - FORECAST_QUANTITY_ROUND_EPS);
  return Math.floor(parsed + FORECAST_QUANTITY_ROUND_EPS);
}

/** Spreadsheet: ROUNDUP(deficit / multiple, 0) * multiple when balance before order is negative. */
function orderQtyToRestoreNonNegative(balanceBeforeOrder: number, orderMultiple: number): number {
  if (balanceBeforeOrder >= -1e-9) return 0;
  const deficit = -balanceBeforeOrder;
  const multiple = Math.max(1, orderMultiple);
  return Math.ceil(deficit / multiple) * multiple;
}

/** First-of-month YYYY-MM-01 from an ISO date, Date, or YYYY-MM-DD string. */
export function forecastMonthKeyFromIsoDate(value: string | Date | null | undefined): string | null {
  if (value == null) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return `${value.getUTCFullYear()}-${String(value.getUTCMonth() + 1).padStart(2, "0")}-01`;
  }
  const text = String(value).trim();
  const match = text.match(/^(\d{4})-(\d{2})/);
  if (!match) return null;
  return `${match[1]}-${match[2]}-01`;
}

/**
 * Recompute monthly roll-forward for one location row.
 * Demand and arrivals are taken from the existing cells.
 */
export function rerollForecastSeriesCells<T extends ForecastRerollCell>(args: {
  cells: T[];
  orderMultiple: number;
  startingQty: number;
  allocatedReserve: number;
}): T[] {
  const nextCells: T[] = [];
  let runningStart = roundUpWholeUnit(args.startingQty);
  for (const priorCell of args.cells) {
    const starting_qty = runningStart;
    const arrivals = roundUpWholeUnit(priorCell.arrivals);
    const demand = roundUpWholeUnit(priorCell.demand);
    const balanceBeforeOrder = starting_qty + arrivals - demand;
    const order_qty = orderQtyToRestoreNonNegative(balanceBeforeOrder, args.orderMultiple);
    const ending_qty = roundUpWholeUnit(balanceBeforeOrder + order_qty);
    const shortfall = Math.max(0, -balanceBeforeOrder);
    nextCells.push({
      ...priorCell,
      month: priorCell.month,
      starting_qty,
      arrivals,
      demand,
      ending_qty,
      reserve_target: args.allocatedReserve,
      shortfall,
      order_qty,
    });
    runningStart = ending_qty;
  }
  return nextCells;
}

function bumpArrivalsByKind(
  existing: { kind: ForecastArrivalKind; quantity: number }[] | undefined,
  kind: ForecastArrivalKind,
  quantityDelta: number,
): { kind: ForecastArrivalKind; quantity: number }[] {
  const byKind = new Map<ForecastArrivalKind, number>();
  for (const row of existing ?? []) {
    byKind.set(row.kind, (byKind.get(row.kind) ?? 0) + row.quantity);
  }
  byKind.set(kind, (byKind.get(kind) ?? 0) + quantityDelta);
  return FORECAST_ARRIVAL_KIND_ORDER.map((orderedKind) => ({
    kind: orderedKind,
    quantity: roundUpWholeUnit(byKind.get(orderedKind) ?? 0),
  }));
}

/**
 * Add or subtract one arrival stream on a single CPN x location x month, then
 * roll the rest of that location row forward. Returns patched=false when that
 * series is not in the cached result (caller should rebuild).
 */
export function applyForecastArrivalDelta<T extends ForecastRerollSeries>(
  series: T[],
  delta: ForecastArrivalDelta,
): { series: T[]; patched: boolean } {
  const monthKey = forecastMonthKeyFromIsoDate(delta.monthKey);
  if (monthKey == null) {
    return { series, patched: false };
  }
  if (delta.quantityDelta === 0) {
    return { series, patched: true };
  }

  const targetIndex = series.findIndex(
    (row) => row.cpn === delta.cpn && row.location_code === delta.locationCode,
  );
  if (targetIndex < 0) {
    return { series, patched: false };
  }

  const target = series[targetIndex];
  if (target == null) {
    return { series, patched: false };
  }

  const cellIndex = target.cells.findIndex((cell) => cell.month === monthKey);
  if (cellIndex < 0) {
    return { series, patched: true };
  }

  const nextCells = target.cells.map((cell, index) => {
    if (index !== cellIndex) return cell;
    return {
      ...cell,
      arrivals: cell.arrivals + delta.quantityDelta,
      arrivals_by_kind: bumpArrivalsByKind(cell.arrivals_by_kind, delta.kind, delta.quantityDelta),
    };
  });
  const startingQty = nextCells[0]?.starting_qty ?? 0;
  const rerolled = rerollForecastSeriesCells({
    cells: nextCells,
    orderMultiple: target.order_multiple,
    startingQty,
    allocatedReserve: target.allocated_reserve,
  });

  const nextSeries = series.slice();
  nextSeries[targetIndex] = {
    ...target,
    cells: rerolled,
  };
  return { series: nextSeries, patched: true };
}
